import React, { useMemo, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { first, get } from 'underscore';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  Collapse,
  useCollapseState,
  ValidatedBootstrapPanel,
  ValidatedPanel,
  ValidatedFixedPanel,
  TextTitle,
  getPanelErrorProps,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { propertyIs } from '@bingads-webui-campaign-react/form-error';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { ValidatedUrlOptionsParameter, ValidatedMobileUrlWithoutCheckbox as ValidatedMobileUrl } from '@bingads-webui-campaign-react/validated-url-input';
import { getUrlOptionsDetails } from '@bingads-webui-campaign-react/url-options';
import { isEmptyUrl } from '@bingads-webui-campaign-react/url-input-fluent';
import { ThirdPartyMeasurementWithValidation, ThirdPartyMeasurementV2WithValidation, formatThirdPartyMeasurementSetting } from '@bingads-webui-campaign-react/third-party-measurement';
import MobileFinalUrlImg from '../media/mobile-url.svg';
import UrlOptionsImg from '../media/campaign-url-options.svg';
import ImpressionsTracking from '../media/impressions-tracking.svg';
import CallToActionImg from '../media/call-to-action.svg';
import ConnectedImage from '../media/connected.svg';
import Disclaimer from '../media/disclaimer.svg';
import { getNativeAdsStyles } from '../styles';
import { getNativeAdPanels } from '../configs';
import { ValidatedCallToAction } from './validated-call-to-action';
import { ImpressionTrackingSetting, formatImpressionTrackingSetting } from './impressions-tracking';
import { disclaimerInfo } from './disclaimer';
import { ValidatedAnchorAd } from './validated-anchor-ad';

export const getFinalUrlCardSummaryDetails = (i18n, finalUrls) => (isEmptyUrl(finalUrls) ? i18n.getString(_TL_('No URL')) : first(finalUrls));

const nativeAdMobileFinalUrlsProp = propertyIs('MobileFinalUrls');
const ServerValidatedMobileFinalUrl = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedMobileUrl, { filter: nativeAdMobileFinalUrlsProp }));

const impressionTrackingSettingsProp = propertyIs('ImpressionTrackingUrls');
const ValidatedImpressionTrackingSetting = withControlErrorHandling(withUnvalidatedControlErrors(ImpressionTrackingSetting, { filter: impressionTrackingSettingsProp }));

export const AdvancedSettings = ({
  mobileFinalUrls,
  trackingUrlTemplate,
  finalUrlSuffix,
  urlCustomParameters,
  impressionTrackingUrls,
  verifiedTrackingSettings,
  biddingScheme,
  CallToAction,
  CallToActionText,
  CallToActionAriaText,
  setNewCTACallback,
  BoostAnchors,
  isDisplay,
  isVideo,
  shouldShowDisclaimerPanel,
}) => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const { Permissions, DynamicPermissions } = serverContext || { };
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const classes = useDefaultStyles(getNativeAdsStyles);

  const [
    allAdvancedSettingsSections,
    advancedSettingsExpandedSections,
    setAdvancedSettingsExpandedSections,
  ] = useCollapseState(
    getNativeAdPanels(serverContext, isVideo, isDisplay).advancedPanels /* all sections */,
    [] /* initially expanded sections */
  );

  const isThirdPartyMeasurementEnabled = useMemo(() => {
    // If is video but biddng scheme is CPV, then return false
    if (isVideo && get(biddingScheme, 'Type') === 'ManualCpv') {
      return false;
    }

    return true;
  }, [biddingScheme, isVideo]);

  const verifiedTrackingSettingsValidationProps = useMemo(() => ({
    data: {
      VerifiedTrackingSettings: verifiedTrackingSettings,
    },
    initialValue: {
      VerifiedTrackingSettings: verifiedTrackingSettings,
    },
  }), [verifiedTrackingSettings]);

  const allAdvancedSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAdvancedSettingsExpandedSections }),
    [setAdvancedSettingsExpandedSections]
  );

  const [advancedSettingsSectionExpanded, setAdvancedSettingsSectionExpanded] = useState(false);
  const onAdvancedSettingsSectionToggle = useCallback((expanded) => {
    setAdvancedSettingsSectionExpanded(expanded);
  }, []);

  const mobileFinalUrlValidationProps = useMemo(() => ({
    data: {
      MobileFinalUrls: mobileFinalUrls,
    },
    initialValue: {
      MobileFinalUrls: mobileFinalUrls,
    },
  }), [mobileFinalUrls]);

  const urlOptions = useMemo(() => ({
    TrackingUrlTemplate: trackingUrlTemplate,
    FinalUrlSuffix: finalUrlSuffix,
    UrlCustomParameters: urlCustomParameters,
  }), [trackingUrlTemplate, finalUrlSuffix, urlCustomParameters]);
  const urlOptionsDetails = useMemo(() => getUrlOptionsDetails(i18n, urlOptions), [urlOptions, i18n]);

  const mobileFinalUrlDetails = useMemo(() => (isEmptyUrl(mobileFinalUrls) ? i18n.getString(_TL_('Mobile URL: None')) : first(mobileFinalUrls)), [i18n, mobileFinalUrls]);

  const serverValidatedMobileFinalUrl = useMemo(() => (
    <ServerValidatedMobileFinalUrl
      i18n={i18n}
      validationProps={mobileFinalUrlValidationProps}
      permissions={permissions}
      validateOnRender
      validateOnDomain
    />
  ), [i18n, mobileFinalUrlValidationProps, permissions]);

  const validatedUrlOptionsParameter = useMemo(() => (
    <ValidatedUrlOptionsParameter
      i18n={i18n}
      validationProps={{
        initialValue: urlOptions,
        data: urlOptions,
      }}
    />
  ), [i18n, urlOptions]);

  const validatedImpressionTrackingSetting = useMemo(() => (
    <ValidatedImpressionTrackingSetting
      i18n={i18n}
      impressionTrackingUrls={impressionTrackingUrls}
    />
  ), [i18n, impressionTrackingUrls]);

  const thirdPartyMeasurementWithValidation = useMemo(() => {
    const ThirdPartyMeasurementComponentWithValidation =
      get(permissions, 'IsMOATEnabled', false) ? ThirdPartyMeasurementV2WithValidation : ThirdPartyMeasurementWithValidation;
    let adType = 'native';
    if (isDisplay) {
      adType = 'display';
    } else if (isVideo) {
      adType = 'video';
    }
    return (
      <ThirdPartyMeasurementComponentWithValidation
        i18n={i18n}
        validationProps={verifiedTrackingSettingsValidationProps}
        adType={adType}
      />
    );
  }, [i18n, verifiedTrackingSettingsValidationProps, permissions, isDisplay, isVideo]);

  const callToAction = useMemo(() => (
    <ValidatedCallToAction
      i18n={i18n}
      permissions={permissions}
      validateOnRender
      data={{
        languageValue: CallToAction && CallToAction.language,
        value: CallToAction && CallToAction.cta,
        ctaText: CallToActionText,
        ctaAriaText: CallToActionAriaText,
      }}
      setNewCTACallback={setNewCTACallback}
    />
  ), [CallToAction, CallToActionText, i18n, permissions, setNewCTACallback, CallToActionAriaText]);

  const anchorAd = useMemo(() => (
    <ValidatedAnchorAd
      validationProps={{
        data: {
          BoostAnchors,
        },
      }}
    />
  ), [BoostAnchors]);

  const advancedInputSections = () => (
    <Collapse allPanelItems={allAdvancedSettingsSections} checkItems skipItemsTypeCheck activeKeys={advancedSettingsExpandedSections} setActiveKeys={setAdvancedSettingsExpandedSections}>
      <ValidatedPanel
        key="mobile-final-url"
        title={<TextTitle titleKey={_TL_('Mobile URL')} topicKey="pop_BA_CampaignCreate_AudAds_MobileURL" details={mobileFinalUrlDetails} icon={MobileFinalUrlImg} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
            expand: allAdvancedSettingsExpand,
            expandKey: 'mobile-final-url',
          }}
      >
        {serverValidatedMobileFinalUrl}
      </ValidatedPanel>
      <ValidatedFixedPanel
        key="call-to-action"
        title={<TextTitle titleKey={_TL_('Call to action')} icon={CallToActionImg} />}
        noOverflowHidden
        relativePosition
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        {callToAction}
      </ValidatedFixedPanel>
      <ValidatedFixedPanel
        key="anchor-ad"
        title={<TextTitle titleKey={_TL_('Anchor the ad')} />}
      >
        {anchorAd}
      </ValidatedFixedPanel>
      <ValidatedPanel
        key="advanced-url-options"
        title={<TextTitle titleKey={_TL_('Advanced URL options')} details={urlOptionsDetails} icon={UrlOptionsImg} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
            expand: allAdvancedSettingsExpand,
            expandKey: 'advanced-url-options',
          }}
      >
        <span className="url-options-descriptions">
          {i18n.getString(_TL_('Set up the tracking and redirect information for your ads.'))}
        </span>
        {validatedUrlOptionsParameter}
      </ValidatedPanel>
      <ValidatedPanel
        key="impression-tracking-URLs"
        title={<TextTitle
          titleKey={_TL_('Impression tracking URLs')}
          details={formatImpressionTrackingSetting(impressionTrackingUrls, i18n)}
          icon={ImpressionsTracking}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
              expand: allAdvancedSettingsExpand,
              expandKey: 'impression-tracking-URLs',
            }}
      >
        {validatedImpressionTrackingSetting}
      </ValidatedPanel>
      {
        isThirdPartyMeasurementEnabled && (
          <ValidatedPanel
            key="third-party-measurement"
            title={<TextTitle
              titleKey={_TL_('Third-party measurement')}
              details={formatThirdPartyMeasurementSetting(verifiedTrackingSettings, i18n)}
              icon={ConnectedImage}
              topicKey="pop_BA_VideoAds_Measurement"
            />}
            attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
            actuationProps={{
              expand: allAdvancedSettingsExpand,
              expandKey: 'third-party-measurement',
            }}
          >
            <div className="third-party-measurement-container">
              {thirdPartyMeasurementWithValidation}
            </div>
          </ValidatedPanel>
        )
      }
      {shouldShowDisclaimerPanel && (
        <ValidatedFixedPanel
          key="disclaimer"
          title={<TextTitle
            titleKey={_TL_('Disclaimer')}
            icon={Disclaimer}
            topicKey="pop_BA_Audience_Disclaimers"
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        >
          {disclaimerInfo(i18n)}
        </ValidatedFixedPanel>
        )}
    </Collapse>
  );

  return (
    <>
      <ValidatedBootstrapPanel
        onToggle={onAdvancedSettingsSectionToggle}
        expanded={advancedSettingsSectionExpanded}
        actuationProps={{
          expand: allAdvancedSettingsExpand,

        }}
        className={classNames(classes.collapsiblePanel, 'advanced-settings-panel')}
        render={() => (
          <>
            <BootstrapPanel.Heading>
              <BootstrapPanel.Title toggle>
                {i18n.getString(_TL_('Advanced settings'))}
              </BootstrapPanel.Title>
            </BootstrapPanel.Heading>
            <BootstrapPanel.Collapse>
              <BootstrapPanel.Body style={{ overflow: 'visible' }}>
                <div className={classNames('advanced-settings-section')}>
                  {advancedInputSections()}
                </div>
              </BootstrapPanel.Body>
            </BootstrapPanel.Collapse>
          </>
          )}
      />
    </>
  );
};

AdvancedSettings.propTypes = {
  finalUrlSuffix: PropTypes.string,
  impressionTrackingUrls: PropTypes.arrayOf(PropTypes.string),
  mobileFinalUrls: PropTypes.string,
  trackingUrlTemplate: PropTypes.string,
  urlCustomParameters: PropTypes.arrayOf(PropTypes.shape({})),
  verifiedTrackingSettings: PropTypes.shape({}),
  biddingScheme: PropTypes.string,
  CallToAction: PropTypes.shape({
    language: PropTypes.string,
    cta: PropTypes.string,
  }),
  CallToActionText: PropTypes.string,
  CallToActionAriaText: PropTypes.string,
  BoostAnchors: PropTypes.shape({}),
  setNewCTACallback: PropTypes.func,
  isDisplay: PropTypes.bool,
  isVideo: PropTypes.bool,
  shouldShowDisclaimerPanel: PropTypes.bool,
};

AdvancedSettings.defaultProps = {
  finalUrlSuffix: undefined,
  mobileFinalUrls: undefined,
  trackingUrlTemplate: undefined,
  urlCustomParameters: undefined,
  impressionTrackingUrls: undefined,
  verifiedTrackingSettings: undefined,
  biddingScheme: null,
  CallToAction: null,
  CallToActionText: undefined,
  CallToActionAriaText: undefined,
  BoostAnchors: undefined,
  setNewCTACallback: null,
  isDisplay: false,
  isVideo: false,
  shouldShowDisclaimerPanel: false,
};
